import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  FormattedHTMLMessage,
  FormattedMessage,
} from '~/techstyle-shared/react-intl';

// Exporting this to use the formatting in Speedy Signup
export const ErrorMessageWrapper = styled.div.attrs({
  'data-autotag': 'quiz_signup_error_msg',
})`
  box-sizing: border-box;
  flex: none;
  width: 100%;
  text-align: center;
  font-weight: 600;
  z-index: 0;
  // 'errorPosition' accounts for overlap of form
  ${({ theme: { sizes, colors, fontSizes, quiz } }) => `
    ${quiz.errorPosition}
    color: ${colors.palette.white};
    background-color: ${colors.error};
    font-size: ${fontSizes.xs}px;
    border-radius: ${sizes.sm}px;
  `}
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const SkipError = styled.div`
  width: 100%;
  text-align: center;
  font-weight: 600;
  ${({ theme: { colors, fontSizes } }) => `
    color: ${colors.error};
    font-size: ${fontSizes.xs}px;
    margin-top: 8px;
  `}
`;

export const QuizSignUpErrors = {
  FIRST_NAME_REQUIRED: 'FIRST_NAME_REQUIRED',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  INVALID_EMAIL: 'INVALID_EMAIL',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  PASSWORDS_DONT_MATCH: 'PASSWORDS_DONT_MATCH',
  GENERIC_ERROR: 'GENERIC_ERROR',
  PASSWORD_MIN_CHAR: 'PASSWORD_MIN_CHAR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  DUPLICATE_ACCOUNT: 'DUPLICATE_ACCOUNT',
  SERVER_ERROR: 'SERVER_ERROR',
  SIZE_TOP_REQUIRED: 'SIZE_TOP_REQUIRED',
  SIZE_BOTTOM_REQUIRED: 'SIZE_BOTTOM_REQUIRED',
  SIZE_BRA_REQUIRED: 'SIZE_BRA_REQUIRED',
  SIZE_BAND_REQUIRED: 'SIZE_BAND_REQUIRED',
  SIZE_CUP_REQUIRED: 'SIZE_CUP_REQUIRED',
  SIZE_BRALETTE_REQUIRED: 'SIZE_BRALETTE_REQUIRED',
  SIZE_LINGERIE_REQUIRED: 'SIZE_LINGERIE_REQUIRED',
  REFERRER_REQUIRED: 'REFERRER_REQUIRED',
  ZIP_CODE_ERROR: 'ZIP_CODE_ERROR',
  BIRTHDAY_ERROR: 'BIRTHDAY_ERROR',
  TERMS_ERROR: 'TERMS_ACKNOWLEDGEMENT_REQUIRED',
  TRIAL_REJECTED: 'TRIAL_REJECTED',
  INVALID_PHONE: 'INVALID_PHONE',
};

const ErrorMessage = ({ errors, isSkipForm = false }) => {
  const error =
    Object.values(errors).length > 1 &&
    Object.values(errors)[0] !== QuizSignUpErrors.TERMS_ERROR
      ? 'GENERIC_ERROR'
      : Object.values(errors)[0];
  const ErrorWrapper = isSkipForm ? SkipError : ErrorMessageWrapper;
  switch (error) {
    case QuizSignUpErrors.TERMS_ERROR:
      return (
        <ErrorWrapper>
          <FormattedHTMLMessage
            id="quiz.registration_screen_terms_error"
            defaultMessage="Please accept the T&C before proceeding."
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.DUPLICATE_ACCOUNT:
      return (
        <ErrorWrapper>
          <FormattedHTMLMessage
            id="quiz.registration_screen_email_w_account_error"
            defaultMessage="Looks like that email already has an account with Yitty or Fabletics! Log in to that account to get access to both brands <i><a href='/login'>here</a></i>."
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.TRIAL_REJECTED:
      return (
        <ErrorWrapper>
          <FormattedHTMLMessage
            id="quiz.trial_email_exists"
            defaultMessage="This email address is already associated with an account. Please use a different email address in order to activate your Trial VIP membership."
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.PASSWORD_MIN_CHAR:
    case QuizSignUpErrors.PASSWORD_REQUIRED:
      return (
        <ErrorWrapper data-autotag="error_password_required">
          <FormattedMessage
            id="quiz.registration_screen_password_error"
            defaultMessage="Try again! Your password must be at least 6 characters long."
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.PASSWORDS_DONT_MATCH:
      return (
        <ErrorWrapper data-autotag="error_passwords_dont_match">
          <FormattedMessage
            id="site_account_profile.passwords_dont_match"
            defaultMessage="Passwords do not match"
          />
        </ErrorWrapper>
      );

    case QuizSignUpErrors.SERVER_ERROR:
    case QuizSignUpErrors.UNKNOWN_ERROR:
      return (
        <ErrorWrapper>
          <FormattedMessage
            id="app_global.404_error_message_yitty"
            defaultMessage="Something went wrong. We are working to fix it ASAP. Please try again!"
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.INVALID_EMAIL:
      return (
        <ErrorWrapper>
          <FormattedMessage
            id="quiz.invalid_email_error"
            defaultMessage="Please input a valid email address."
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.ZIP_CODE_ERROR:
      return (
        <ErrorWrapper>
          <FormattedMessage
            id="quiz.sizing_screen_zip_code_invalid"
            defaultMessage="Please enter a valid Zip Code"
          />
        </ErrorWrapper>
      );
    case QuizSignUpErrors.FIRST_NAME_REQUIRED:
    case QuizSignUpErrors.EMAIL_REQUIRED:
    case QuizSignUpErrors.GENERIC_ERROR:
      return (
        <ErrorWrapper>
          <FormattedMessage
            id="quiz.registration_screen_generic_error"
            defaultMessage="Please fix errors before continuing"
          />
        </ErrorWrapper>
      );
    default:
      return null;
  }
};

ErrorMessage.propTypes = {
  errors: PropTypes.object,
  isSkipForm: PropTypes.bool,
};

export default ErrorMessage;
